import { useContext, useEffect, useState } from "react";
import { BsCalendar3 } from "react-icons/bs";
import { Col, Form, Modal, Row } from "react-bootstrap";
import DatePicker, { registerLocale } from "react-datepicker";
import pt from "date-fns/locale/pt";
import styled from "styled-components";
import { PrimaryPurpleButton, SecondaryGreenButton } from "../../../components/Buttons/styles";
import { ReportDetailsContext, ReportFilters, FinancialReportFilters } from "../../../context/admin/reportDetails/ReportDetailsContext";
import { CourseMultiSelectDropdown } from "./components/CourseMultiSelectDropdown";
import { StudentAutocomplete } from "./components/StudentAutocomplete";
import { ReportDetailEnum } from "../../../domain/enum/ReportDetailEnum";

registerLocale("pt", pt);

interface FilterReportModalProps {
    show: boolean;
    showModuleDates?: boolean;
    onClose: () => void;
}

export function FilterReportModal({ show, showModuleDates = false, onClose }: FilterReportModalProps) {
    const {
        activeTab,
        filters, applyFilters, clearFilters,
        indicatorsFilters, applyIndicatorsFilters, clearIndicatorsFilters,
        financialFilters, applyFinancialFilters, clearFinancialFilters,
        courseOptions, loadCourseOptions,
    } = useContext(ReportDetailsContext);

    const isIndicatorsTab = activeTab === ReportDetailEnum.consumptionDetailed;
    const isFinancialTab = activeTab === ReportDetailEnum.financial;

    const [selectedCourseIds, setSelectedCourseIds] = useState<string[]>([]);
    const [studentName, setStudentName] = useState("");
    const [moduleStartDateFrom, setModuleStartDateFrom] = useState<Date | null>(null);
    const [moduleStartDateTo, setModuleStartDateTo] = useState<Date | null>(null);
    const [moduleEndDateFrom, setModuleEndDateFrom] = useState<Date | null>(null);
    const [moduleEndDateTo, setModuleEndDateTo] = useState<Date | null>(null);
    const [purchaseDateFrom, setPurchaseDateFrom] = useState<Date | null>(null);
    const [purchaseDateTo, setPurchaseDateTo] = useState<Date | null>(null);
    const [dateError, setDateError] = useState<string | null>(null);

    useEffect(() => {
        if (show) {
            if (isFinancialTab) {
                setSelectedCourseIds(financialFilters.courseIds);
                setStudentName(financialFilters.studentName);
                setPurchaseDateFrom(financialFilters.purchaseDateFrom);
                setPurchaseDateTo(financialFilters.purchaseDateTo);
            } else {
                const activeFilters = isIndicatorsTab ? indicatorsFilters : filters;
                setSelectedCourseIds(activeFilters.courseIds);
                setStudentName(activeFilters.studentName);
                setModuleStartDateFrom(activeFilters.moduleStartDateFrom);
                setModuleStartDateTo(activeFilters.moduleStartDateTo);
                setModuleEndDateFrom(activeFilters.moduleEndDateFrom);
                setModuleEndDateTo(activeFilters.moduleEndDateTo);
            }
            setDateError(null);
        }
    }, [show]);

    const validateDateRange = (from: Date | null, to: Date | null): boolean => {
        if (!from || !to) return true;
        const diffMs = to.getTime() - from.getTime();
        const diffDays = diffMs / (1000 * 60 * 60 * 24);
        return diffDays <= 365;
    };

    const handleFilter = () => {
        if (isFinancialTab) {
            if (!validateDateRange(purchaseDateFrom, purchaseDateTo)) {
                setDateError("Intervalo máximo de 365 dias");
                return;
            }
            setDateError(null);
            const newFinancialFilters: FinancialReportFilters = {
                courseIds: selectedCourseIds,
                studentName,
                purchaseDateFrom,
                purchaseDateTo,
            };
            applyFinancialFilters(newFinancialFilters);
            return;
        }

        if (!validateDateRange(moduleStartDateFrom, moduleStartDateTo)) {
            setDateError("Intervalo máximo de 365 dias");
            return;
        }
        if (!validateDateRange(moduleEndDateFrom, moduleEndDateTo)) {
            setDateError("Intervalo máximo de 365 dias");
            return;
        }

        setDateError(null);

        const newFilters: ReportFilters = {
            courseIds: selectedCourseIds,
            studentName,
            moduleStartDateFrom,
            moduleStartDateTo,
            moduleEndDateFrom,
            moduleEndDateTo,
        };

        if (isIndicatorsTab) {
            applyIndicatorsFilters(newFilters);
        } else {
            applyFilters(newFilters);
        }
    };

    const handleClose = () => {
        if (isFinancialTab) {
            clearFinancialFilters();
        } else if (isIndicatorsTab) {
            clearIndicatorsFilters();
        } else {
            clearFilters();
        }
        onClose();
    };

    return (
        <FilterReportModalStyles show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Filtrar relatório</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="subtitle">Busque por nome de aluno, curso ou período</p>

                <Form.Group className="mb-3">
                    <Form.Label className="field-label">Aluno</Form.Label>
                    <StudentAutocomplete
                        value={studentName}
                        onSelect={(_id, name) => setStudentName(name)}
                        onClear={() => setStudentName("")}
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label className="field-label">Curso</Form.Label>
                    <CourseMultiSelectDropdown
                        options={courseOptions}
                        selectedIds={selectedCourseIds}
                        onChange={setSelectedCourseIds}
                        onOpen={loadCourseOptions}
                    />
                </Form.Group>

                {isFinancialTab && (
                    <>
                        {dateError && (
                            <p className="date-error">{dateError}</p>
                        )}
                        <Form.Group className="mb-3">
                            <Form.Label className="field-label">Data da Compra</Form.Label>
                            <Row>
                                <Col sm={6}>
                                    <Form.Label className="sub-label">Data início</Form.Label>
                                    <div className="date-wrapper">
                                        <DatePicker
                                            className="input-date w-100"
                                            placeholderText="dd/mm/aaaa"
                                            dateFormat="dd/MM/yyyy"
                                            selectsRange={false}
                                            locale="pt"
                                            selected={purchaseDateFrom}
                                            onChange={(date) => setPurchaseDateFrom(date as Date)}
                                            todayButton="Hoje"
                                        />
                                        <BsCalendar3 className="icon-calendar" />
                                    </div>
                                </Col>
                                <Col sm={6}>
                                    <Form.Label className="sub-label">Data fim</Form.Label>
                                    <div className="date-wrapper">
                                        <DatePicker
                                            className="input-date w-100"
                                            placeholderText="dd/mm/aaaa"
                                            dateFormat="dd/MM/yyyy"
                                            selectsRange={false}
                                            locale="pt"
                                            selected={purchaseDateTo}
                                            minDate={purchaseDateFrom ?? undefined}
                                            onChange={(date) => setPurchaseDateTo(date as Date)}
                                            todayButton="Hoje"
                                        />
                                        <BsCalendar3 className="icon-calendar" />
                                    </div>
                                </Col>
                            </Row>
                            <p className="date-hint mt-1">Intervalo máximo de seleção é de 365 dias entre início e fim</p>
                        </Form.Group>
                    </>
                )}

                {showModuleDates && (
                    <>
                        {dateError && (
                            <p className="date-error">{dateError}</p>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label className="field-label">Data de Início do Módulo</Form.Label>
                            <Row>
                                <Col sm={6}>
                                    <Form.Label className="sub-label">Data início</Form.Label>
                                    <div className="date-wrapper">
                                        <DatePicker
                                            className="input-date w-100"
                                            placeholderText="dd/mm/aaaa"
                                            dateFormat="dd/MM/yyyy"
                                            selectsRange={false}
                                            locale="pt"
                                            selected={moduleStartDateFrom}
                                            onChange={(date) => setModuleStartDateFrom(date as Date)}
                                            todayButton="Hoje"
                                        />
                                        <BsCalendar3 className="icon-calendar" />
                                    </div>
                                </Col>
                                <Col sm={6}>
                                    <Form.Label className="sub-label">Data fim</Form.Label>
                                    <div className="date-wrapper">
                                        <DatePicker
                                            className="input-date w-100"
                                            placeholderText="dd/mm/aaaa"
                                            dateFormat="dd/MM/yyyy"
                                            selectsRange={false}
                                            locale="pt"
                                            selected={moduleStartDateTo}
                                            minDate={moduleStartDateFrom ?? undefined}
                                            onChange={(date) => setModuleStartDateTo(date as Date)}
                                            todayButton="Hoje"
                                        />
                                        <BsCalendar3 className="icon-calendar" />
                                    </div>
                                </Col>
                            </Row>
                            <p className="date-hint mt-1">Intervalo máximo de seleção é de 365 dias entre início e fim</p>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label className="field-label">Data de Término do Módulo</Form.Label>
                            <Row>
                                <Col sm={6}>
                                    <Form.Label className="sub-label">Data início</Form.Label>
                                    <div className="date-wrapper">
                                        <DatePicker
                                            className="input-date w-100"
                                            placeholderText="dd/mm/aaaa"
                                            dateFormat="dd/MM/yyyy"
                                            selectsRange={false}
                                            locale="pt"
                                            selected={moduleEndDateFrom}
                                            onChange={(date) => setModuleEndDateFrom(date as Date)}
                                            todayButton="Hoje"
                                        />
                                        <BsCalendar3 className="icon-calendar" />
                                    </div>
                                </Col>
                                <Col sm={6}>
                                    <Form.Label className="sub-label">Data fim</Form.Label>
                                    <div className="date-wrapper">
                                        <DatePicker
                                            className="input-date w-100"
                                            placeholderText="dd/mm/aaaa"
                                            dateFormat="dd/MM/yyyy"
                                            selectsRange={false}
                                            locale="pt"
                                            selected={moduleEndDateTo}
                                            minDate={moduleEndDateFrom ?? undefined}
                                            onChange={(date) => setModuleEndDateTo(date as Date)}
                                            todayButton="Hoje"
                                        />
                                        <BsCalendar3 className="icon-calendar" />
                                    </div>
                                </Col>
                            </Row>
                            <p className="date-hint mt-1">Intervalo máximo de seleção é de 365 dias entre início e fim</p>
                        </Form.Group>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Row>
                    <Col>
                        <SecondaryGreenButton className="px-4" onClick={handleClose}>
                            Cancelar
                        </SecondaryGreenButton>
                    </Col>
                    <Col>
                        <PrimaryPurpleButton className="px-4" onClick={handleFilter}>
                            Buscar
                        </PrimaryPurpleButton>
                    </Col>
                </Row>
            </Modal.Footer>
        </FilterReportModalStyles>
    );
}

const FilterReportModalStyles = styled(Modal)`
    .modal-content {
        border-radius: 1.11vmin;
        border: 1px solid #E0E0E0;
        box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);
    }

    .modal-header {
        border-bottom: none;
        padding-bottom: 0;

        .modal-title {
            font-family: Lato;
            font-weight: 700;
            font-size: 1.2rem;
            color: #514F4E;
        }
    }

    .subtitle {
        font-size: 0.8rem;
        font-weight: 400;
        color: #7A7A7A;
        margin-bottom: 1rem;
    }

    .field-label {
        font-family: Lato;
        font-weight: 500;
        font-size: 0.85rem;
        color: #514F4E;
    }

    .sub-label {
        font-size: 0.75rem;
        font-weight: 400;
        color: #7A7A7A;
    }

    .date-wrapper {
        position: relative;
        display: flex;
        align-items: center;

        .react-datepicker-wrapper {
            width: 100%;
        }

        .icon-calendar {
            position: absolute;
            right: 10px;
            top: 50%;
            transform: translateY(-50%);
            color: #7A7A7A;
            pointer-events: none;
            z-index: 0;
        }

        .input-date {
            width: 100%;
            border: 1px solid #b3b5df;
            border-radius: 8px;
            padding: 0.375rem 2rem 0.375rem 0.75rem;
            font-family: 'Lato', sans-serif;
            font-weight: 400;
            font-size: 0.85rem;
            color: #514F4E;
            min-height: 38px;
            background: #fff;
            outline: none;
            transition: border-color 0.15s, box-shadow 0.15s;

            &:focus {
                border-color: #6468C8;
                box-shadow: 0px 0px 4px #6468c8;
            }

            &::placeholder {
                font-family: 'Lato', sans-serif !important;
                font-weight: 400 !important;
                font-size: 0.85rem !important;
                color: #808080 !important;
            }
        }
    }

    .date-hint {
        font-size: 0.7rem;
        font-weight: 400;
        color: #7A7A7A;
    }

    .date-error {
        font-size: 0.8rem;
        font-weight: 400;
        color: #D30808;
        margin-bottom: 0.5rem;
    }

    .modal-footer {
        border-top: none;
        justify-content: center;
        gap: 1rem;

        button {
            height: 3rem;
            width: 10rem;
            font-size: 2vmin;
            i {
                font-size: 3.33vmin;
            }
        }
    }
`;
